// domain/constants/fundingSource.js

/*
 * The legacy `fundingSourceType` conflated at least three axes — what kind of organization pays,
 * how they are billed, and which program rules apply — into one string. Its values show the
 * damage: `insurance` names an organization kind, `privatePay` names a payment arrangement, and
 * `regionalCenter` names a California program. Nothing could branch on it honestly.
 *
 * It is decomposed here into the two axes the engine actually needs, and they are separate
 * because they vary independently: a school district billing Medicaid files claims, and a school
 * district paying from its own budget does not. Same archetype, different channel.
 */

/**
 * What KIND of funder this is.
 *
 * Drives whether there is a policyholder, whether coordination of benefits applies, and what
 * "authorization" means. It does NOT decide how the funder is billed — see BILLING_CHANNEL, which
 * is what conditional billing settings key on.
 *
 * `null` is a legitimate stored value, not an absence to be defaulted away: the migration cannot
 * decompose the legacy `other` and `regionalCenter` records, so it leaves the axis genuinely
 * unset rather than inventing a value that might later go load-bearing. Nothing resolves through
 * archetype today, so an unset one is inert until someone edits the funder.
 *
 * @domainObject value-object
 * @invariant Extension is additive. A new kind of funder is a new member, never a reshape of
 *   funders already stored under an existing one.
 * @invariant Names an organization kind, never a billing mechanism. A member that answers "how
 *   are they billed" belongs in BILLING_CHANNEL — that conflation is what this replaced.
 * @typedef {'commercial_insurer' | 'government_program' | 'public_entity' | 'self_pay'
 *   | 'grant'} FundingSourceArchetype
 */
export const FUNDING_SOURCE_ARCHETYPE = {
  COMMERCIAL_INSURER: 'commercial_insurer',
  GOVERNMENT_PROGRAM: 'government_program',
  PUBLIC_ENTITY: 'public_entity',
  SELF_PAY: 'self_pay',
  GRANT: 'grant',
};

/**
 * HOW this funder is billed — the mechanism, and the load-bearing discriminator.
 *
 * Conditional settings gate on this rather than on the archetype, because archetype-is-insurance
 * is a proxy that is wrong at the edges: a school district billing Medicaid files claims and is
 * not an insurer. "Who bills" only means something when the mechanism names a rendering provider,
 * which 837P does and an invoice does not.
 *
 * Members name a SPECIFIC mechanism rather than an abstracted outcome. `edi_837p` rather than
 * "files claims": a global-sounding word that hides a US assumption is worse than an honestly
 * specific one, since other countries file claims under other standards. Those arrive as new
 * members alongside this one, never as a generalization of it.
 *
 * @domainObject value-object
 * @invariant Extension is additive, and non-US mechanisms join as peers rather than by
 *   generalizing an existing member into something country-neutral.
 * @typedef {'edi_837p' | 'invoice' | 'roster'} BillingChannel
 */
export const BILLING_CHANNEL = {
  EDI_837P: 'edi_837p',
  INVOICE: 'invoice',
  ROSTER: 'roster',
};

/**
 * The channel an archetype pre-fills, so the common case is "pick the archetype and move on".
 *
 * A DEFAULT, not a derivation — the two axes are independent and the specialist can always
 * override. `public_entity` defaults to invoice because a district paying from its own budget is
 * the common case; the district billing Medicaid is the edge case this map deliberately gets
 * wrong so that the override stays a visible act.
 *
 * No archetype defaults to `roster`: a roster arrangement is something you know you are in, never
 * something to be assumed from the kind of organization.
 */
export const DEFAULT_BILLING_CHANNEL_BY_ARCHETYPE = {
  [FUNDING_SOURCE_ARCHETYPE.COMMERCIAL_INSURER]: BILLING_CHANNEL.EDI_837P,
  [FUNDING_SOURCE_ARCHETYPE.GOVERNMENT_PROGRAM]: BILLING_CHANNEL.EDI_837P,
  [FUNDING_SOURCE_ARCHETYPE.PUBLIC_ENTITY]: BILLING_CHANNEL.INVOICE,
  [FUNDING_SOURCE_ARCHETYPE.SELF_PAY]: BILLING_CHANNEL.INVOICE,
  [FUNDING_SOURCE_ARCHETYPE.GRANT]: BILLING_CHANNEL.INVOICE,
};

/**
 * WHO the payer names as the rendering provider on a claim.
 *
 * `performer` is the person who ran the session. `group` means the billing organization is named
 * with NO individual rendering provider — agency and waiver billing, where 837P carries the billing
 * provider in 2010AA and omits the 2310B rendering loop. That makes `group` the one value where a
 * null rendering provider is correct rather than a hold, which is a projector change and not this
 * bet's.
 *
 * `supervisor` IS DELIBERATELY ABSENT, and its absence is the honest answer rather than an omission.
 * "Supervisor" hides at least two different billers — the client's case manager / lead BCBA, which is
 * a client-level attribute, and the person who supervised that particular session, which is an
 * encounter fact. They resolve to different people from different sources, and the projector refuses
 * to model `supervisingProviderId` for exactly that reason. Config matches that refusal instead of
 * picking one and being quietly wrong for the other. A funder whose real rule is supervisor-bills
 * therefore CANNOT be fully configured yet; that is a known deferred case, not a bug. When the
 * discovery settles, the value joins as a new member — an enum addition, not a reshape.
 *
 * ALWAYS RECORDED WHERE IT MEANS SOMETHING, and `performer` is the default. Both ends are under our
 * control — the migration writes every existing funder and the editor writes every new one — so the
 * field can be guaranteed present rather than merely usually present, which is what lets the editor
 * use a binary control instead of a three-state one nobody wants to operate.
 *
 * So `null` does NOT mean "not recorded". It means the funder's billing channel names no rendering
 * provider at all — an invoice or a roster has nowhere to put one. That is the only reason the field
 * is ever absent, and it is a fact about the channel rather than a gap in the record.
 *
 * The residual risk is worth naming rather than hiding: `performer` is a default, not a verified
 * policy, and payers that will not credential the performer to bill independently exist. Nothing
 * consumes this yet — the projector still reads its own empty `renderingRules` map — so whoever
 * wires that up inherits the question of how much to trust a seeded value.
 *
 * @domainObject value-object
 * @invariant Extension is additive; a newly understood biller is a new member, never a redefinition
 *   of an existing one.
 * @invariant Null where the billing channel names no rendering provider — there is nothing for the
 *   field to hold. Where the channel DOES name one, null means nobody has chosen yet, and the editor
 *   refuses to save a funder in that state rather than choosing for them.
 * @invariant Never defaulted. `performer` is the commonest answer and not a safe one: a funder that
 *   must bill under a supervising provider, silently marked otherwise, produces a claim naming an NPI
 *   the payer rejects — and nothing on the record would show the choice had been made for it.
 * @typedef {'performer' | 'group'} BillsUnder
 */
export const BILLS_UNDER = {
  PERFORMER: 'performer',
  GROUP: 'group',
};

/**
 * Does this billing channel name a rendering provider at all?
 *
 * The gate for whether "who bills" is even a question. Electronic claims name one; an invoice and a
 * roster do not, so asking a self-pay funder who renders is asking about a field its claims have no
 * room for.
 *
 * KEYED ON THE CHANNEL, NOT THE ARCHETYPE, and that is the whole reason the two axes were separated.
 * `archetype === 'commercial_insurer'` is a proxy that is wrong at the edges: a school district
 * billing Medicaid is a public entity that files claims. The mechanism is what decides.
 *
 * @param {string|null} billingChannel
 * @returns {boolean}
 */
export function namesRenderingProvider(billingChannel) {
  return billingChannel === BILLING_CHANNEL.EDI_837P;
} // namesRenderingProvider()

/**
 * Does this billing channel carry a prior authorization at all?
 *
 * The gate for whether "does this payer require authorization" is even a question. Prior
 * authorization is an ADJUDICATION concept: a payer agrees in advance to cover work, and the claim
 * references that agreement. An invoice is not adjudicated — the work is billed as delivered — and a
 * roster is a headcount arrangement, so asking either whether it requires authorization is asking
 * about a permission that does not exist in that relationship.
 *
 * SEPARATE FROM `namesRenderingProvider` DESPITE THE IDENTICAL ANSWER TODAY, and deliberately so.
 * The two ask different questions — whether the bill has a field for a renderer, and whether the
 * relationship has an adjudicator — and they agree only because 837P is currently the one adjudicated
 * channel. Collapsing them into one predicate would rebuild in miniature exactly the conflation the
 * archetype/channel split was made to undo, and would silently mislead the day a channel answers the
 * two differently.
 *
 * @param {string|null} billingChannel
 * @returns {boolean}
 */
export function carriesPriorAuthorization(billingChannel) {
  return billingChannel === BILLING_CHANNEL.EDI_837P;
} // carriesPriorAuthorization()

/** Funding sources are inactivated rather than deleted, so a coverage can still name its payer. */
export const FUNDING_SOURCE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

/**
 * The telehealth modifiers a payer may ask for, as claim codes.
 *
 * A LIST RATHER THAN A NAMED VOCABULARY, because the code IS the name. `95` and `GT` both mean a
 * synchronous audio-video encounter; which one a payer wants is a fact about that payer's manual,
 * not a distinction with a domain meaning we could name honestly.
 *
 * PAYER-LEVEL, NOT PER PLACE OR PER SERVICE. The modifier describes the MODALITY of the encounter,
 * so a payer that says "95 for telehealth" means 95 on any telehealth claim. Per-place would mean
 * setting it once per telehealth place record and being able to set them inconsistently; per-service
 * would model a variation no payer sheet has been shown to make. What DOES vary by service is
 * whether telehealth is permitted at all, which is coverage rather than coding and is not modeled.
 *
 * AN ABSENT VALUE MEANS NO MODIFIER, and there is deliberately no separate "unconfigured" state:
 * a payer nobody has configured and a payer that wants the place-of-service code alone produce the
 * same claim, so the difference is provenance nothing reads — and modelling it would make somebody
 * open a field to say they are not using one.
 *
 * @invariant Extension is additive. A newly encountered modifier joins as a member; existing stored
 *   values never change meaning.
 * @typedef {'95' | 'GT'} TelehealthModifier
 */
export const TELEHEALTH_MODIFIERS = ['95', 'GT'];

/**
 * Is this a telehealth modifier this system will store? Null and absent are NOT — they are the
 * no-modifier answer, which the caller handles rather than validating.
 *
 * @param {string|null} value
 * @returns {boolean}
 */
export function isTelehealthModifier(value) {
  return TELEHEALTH_MODIFIERS.includes(value);
} // isTelehealthModifier()
